// BWC Terminal — Core $ Stop methodology calculations

export function parseEquity(val) {
  if (!val) return 0
  const s = String(val).trim().toLowerCase()
  const num = parseFloat(s)
  if (s.endsWith('b')) return num * 1_000_000_000
  if (s.endsWith('m')) return num * 1_000_000
  if (s.endsWith('k')) return num * 1_000
  return num || 0
}

export function fmt(val) {
  if (!val && val !== 0) return '—'
  const abs = Math.abs(val)
  const sign = val < 0 ? '-' : ''
  if (abs >= 1_000_000) return sign + '$' + (abs / 1_000_000).toFixed(2) + 'M'
  if (abs >= 1_000) return sign + '$' + (abs / 1_000).toFixed(1) + 'k'
  return sign + '$' + abs.toFixed(2)
}

export function fmtPct(val) {
  if (val === null || val === undefined) return '—'
  return val.toFixed(2) + '%'
}

export function calcTrancheSizing(equity, allocPct) {
  return equity * (allocPct / 100)
}

export function calcMaxLoss(equity, allocPct, riskOfTranchePct) {
  const riskPct = riskOfTranchePct * allocPct / 100
  return equity * (riskPct / 100)
}

export function calcShares(trancheBudget, entryPrice) {
  if (!entryPrice || entryPrice <= 0) return 0
  return Math.floor(trancheBudget / entryPrice)
}

export function calcUnifiedStop(tranches, riskOfTranchePct) {
  if (!tranches || tranches.length === 0) return null
  let totalShares = 0
  let totalCost = 0
  tranches.forEach(t => {
    totalShares += t.shares
    totalCost += t.shares * t.price
  })
  if (totalShares === 0) return null
  const avgCost = totalCost / totalShares
  const firstTrancheActualCost = tranches[0].shares * tranches[0].price
  const actualMaxLoss = firstTrancheActualCost * (riskOfTranchePct / 100)
  const stopPrice = avgCost - (actualMaxLoss / totalShares)
  return {
    stopPrice,
    avgCost,
    totalShares,
    totalCost,
    actualMaxLoss,
    stopDistPct: ((avgCost - stopPrice) / avgCost) * 100,
  }
}

export function calcProfitTarget(stopCalc, tranches) {
  if (!stopCalc || !tranches.length) return null
  const { avgCost, totalShares, actualMaxLoss } = stopCalc
  const gainPerShareNeeded = (2 * actualMaxLoss) / totalShares
  const targetPrice = avgCost + gainPerShareNeeded
  const sharesToSell = Math.round(totalShares / 3)
  const pctGainFromAvg = ((targetPrice - avgCost) / avgCost) * 100
  return {
    targetPrice,
    sharesToSell,
    sellValue: sharesToSell * targetPrice,
    gainAtTarget: totalShares * (targetPrice - avgCost),
    pctGainFromAvg,
  }
}

export function checkStopBreach(stopCalc, maxStopPct = 7.5) {
  if (!stopCalc) return false
  return stopCalc.stopDistPct > maxStopPct + 0.05
}

export function calcRMultiple(entryPrice, exitPrice, stopPrice) {
  const risk = entryPrice - stopPrice
  if (risk <= 0) return null
  return (exitPrice - entryPrice) / risk
}

export function calcPerformanceStats(closedTrades) {
  if (!closedTrades || closedTrades.length === 0) return null
  const returns = closedTrades.map(t => t.pnl)
  const winners = returns.filter(r => r > 0)
  const losers = returns.filter(r => r < 0)
  const totalPnL = returns.reduce((a, b) => a + b, 0)
  const winRate = (winners.length / returns.length) * 100
  const avgWin = winners.length ? winners.reduce((a, b) => a + b, 0) / winners.length : 0
  const avgLoss = losers.length ? Math.abs(losers.reduce((a, b) => a + b, 0) / losers.length) : 0
  const expectancy = avgWin * (winRate / 100) - avgLoss * (1 - winRate / 100)
  let peak = 0, maxDD = 0, running = 0
  returns.forEach(r => {
    running += r
    if (running > peak) peak = running
    const dd = peak - running
    if (dd > maxDD) maxDD = dd
  })
  return {
    totalPnL,
    tradeCount: returns.length,
    winRate,
    avgWin,
    avgLoss,
    profitFactor: avgLoss > 0 ? avgWin / avgLoss : null,
    expectancy,
    maxDrawdown: maxDD,
  }
}
